import { useEffect, useMemo, useState } from 'react';
import { withWindow } from '@shared/utils/withWindow';
import { computeContainerStyle } from './computeContainerStyle';
import { fetchThemeStyles } from './fetchThemeStyles';
import { useBundledApplicatorEnabled, useSeededThemeStyles } from './ThemeStylesProvider';
import { ensureUserPreferences } from './internal/utils/ensureUserPreferences';
import { pickCanvas, pickFrameSprites } from './themeCanvasSelection';
import type { ApplicationContext, UserPreferencesInput } from './styleApplicator.types';
import type { Sprite, ThemeOutput } from './themeOutput.types';

export type ThemeCanvasResult = Readonly<{
  /** Sprites for this canvas, in application order. Empty until loaded (or when skipped). */
  sprites: ReadonlyArray<Sprite>;
  /** Frame background/text colors per the canvas settings + preferences. */
  containerStyle: React.CSSProperties | undefined;
  /** True while the theme JSON is loading. False when skipped. */
  loading: boolean;
}>;

const EMPTY_SPRITES: ReadonlyArray<Sprite> = [];

// Matches the legacy shouldApplyTextColor.
const shouldApplyTextColor = (context: ApplicationContext): boolean => {
  return ('component' in context && context.component === 'title-banner') || ('composition' in context && context.composition === 'simple');
};

/**
 * The React way into a theme canvas (PRNT-4898): fetch the theme's style JSON (cached and
 * deduped per themeId+devicePixelRatio in fetchThemeStyles) and derive the sprites + frame
 * colors for one composition layer or themeable component. Inert unless the
 * websiteBundleStyleApplicator flag is affirmatively 'on' — with the flag off or unresolved the
 * legacy external applicator owns the page exactly as it does on main.
 */
export const useThemeCanvas = (themeId: string | undefined | null, context: ApplicationContext, preferences?: UserPreferencesInput): ThemeCanvasResult => {
  // Latched for the whole page, so this can never switch on after useStyleApplicator has
  // already committed to the legacy path (which would paint both).
  const enabled = useBundledApplicatorEnabled() && !!themeId;

  // Styles inlined by SSR are available on the very first render — server and client both
  // start from them, so sprites appear in the markup and hydration matches (PRNT-4898).
  const seededAll = useSeededThemeStyles(enabled ? themeId : undefined, context);
  // Only treat the seed as authoritative for canvases it actually contains: the payload is
  // pruned to what the server rendered, so another breakpoint's canvas must still fetch.
  const seededStyles = seededAll && pickCanvas(seededAll, context) ? seededAll : undefined;

  // Fetched data is keyed by the theme it belongs to, so switching themes (the admin designer
  // does this live) can never show the previous theme's art or report `loading: false` while
  // the new one is still in flight. A settled entry with no `data` means "tried and failed".
  const [fetched, setFetched] = useState<Readonly<{ themeId: string; data?: ThemeOutput }> | undefined>(undefined);
  const isFetchCurrent = !!themeId && fetched?.themeId === themeId;
  const data = seededStyles ?? (isFetchCurrent ? fetched?.data : undefined);
  const loading = enabled && !seededStyles && !isFetchCurrent;

  useEffect(() => {
    if (!enabled || !themeId || seededStyles) {
      return;
    }
    let cancelled = false;
    fetchThemeStyles(
      themeId,
      withWindow(win => win.devicePixelRatio, 1),
    )
      .then(themeData => {
        if (!cancelled) {
          setFetched({ themeId, data: themeData });
        }
      })
      .catch(() => {
        // Degrades to no art, same as a failed applicator.js load on the legacy path;
        // fetchThemeStyles evicts the failed entry so a later mount can retry.
        if (!cancelled) {
          setFetched({ themeId });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, themeId, seededStyles]);

  // Context objects are inline literals at call sites; key on their values.
  const contextKey = 'component' in context ? `component:${context.component}` : `${context.composition}:${context.layer}`;

  return useMemo((): ThemeCanvasResult => {
    if (!enabled || !data) {
      return { sprites: EMPTY_SPRITES, containerStyle: undefined, loading };
    }
    // Old theme JSONs exist with divergent shapes (midnight_rose predates `settings`, for
    // one) — the legacy applicator tolerated them via try/catch, so a malformed theme must
    // degrade to "no art / no colors" here too, never a render crash.
    try {
      const canvas = pickCanvas(data, context);
      if (!canvas || !canvas.sprites) {
        return { sprites: EMPTY_SPRITES, containerStyle: undefined, loading: false };
      }
      const ensuredPreferences = ensureUserPreferences(preferences);
      return {
        // A seeded page reads the @2x JSON on every device; per-device sizing is the <img srcset>
        // ladder ThemeSprites derives from each sprite (see spriteImage.ts, themeArtRecorder.ts).
        sprites: pickFrameSprites(canvas.sprites),
        containerStyle: canvas.settings ? computeContainerStyle(canvas.settings, ensuredPreferences, shouldApplyTextColor(context)) : undefined,
        loading: false,
      };
    } catch {
      return { sprites: EMPTY_SPRITES, containerStyle: undefined, loading: false };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, data, loading, contextKey, preferences]);
};
